import { useMemo } from 'react';

import { State } from 'modules';

import { bindActionCreators, useDispatch } from 'third-party';

import { DEFAULT_PAGE_SIZE } from 'constants/common';
import { tenantEndpoint, tenantSingleEndpoint } from 'constants/endpoints';

import { getPageNumberAfterLoadMore } from 'utils/helper';
import { httpMethod, simplifyBuilder } from 'utils/sra';

import {
  APITenantSearchFilter,
  APITenantSearchResult,
  APISortOrder,
  APITenant,
} from 'types/api';

export type TenantState = {
  tenantsList: APITenant[];
  tenant: APITenant | null;
  loadTenantPending: boolean;
  loadTenantsPending: boolean;
  allTenantsLoaded: boolean;
  tenantsListLoaded: boolean;
  updateTenantPending: boolean;
  createTenantPending: boolean;
  filters: APITenantSearchFilter;
  pagination: {
    pageNumber: APITenantSearchResult['pageNumber'];
    pageSize: APITenantSearchResult['pageSize'];
  };
};

export const tenantState: TenantState = {
  tenantsList: [],
  tenant: null,
  loadTenantPending: false,
  updateTenantPending: false,
  createTenantPending: false,
  filters: {
    searchSortOrders: [
      {
        sortField: 'createdOn',
        sortOrder: APISortOrder.DESC,
      },
    ],
  },
  loadTenantsPending: false,
  tenantsListLoaded: false,
  allTenantsLoaded: false,
  pagination: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
};

const builder = simplifyBuilder(tenantState, {});

const updateTenantFilters = builder.createReduxAction(
  (filters: APITenantSearchFilter) => ({
    name: 'updateTenantFilters',
    updater: () => ({
      filters,
    }),
  }),
);

export const clearTenantFilters = builder.createReduxAction(() => ({
  name: 'clearTenantFilters',
  updater: () => ({
    filters: {
      searchSortOrders: [
        {
          sortField: 'createdOn',
          sortOrder: APISortOrder.DESC,
        },
      ],
    },
  }),
}));

const resetTenantFilters = builder.createReduxAction(() => ({
  name: 'resetTenantFilters',
  updater: () => ({
    filters: tenantState.filters,
    pagination: tenantState.pagination,
  }),
}));

export const loadTenants = builder.createServerAction(() => ({
  name: 'loadTenants',
  url: tenantEndpoint(),
  body: (state: State) => ({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    ...state.tenant.filters,
  }),
  method: httpMethod.post,
  onSuccess: (
    state: TenantState,
    payload: APITenantSearchResult,
  ) => ({
    tenantsList: payload.result,
    tenantsListLoaded: true,
    allTenantsLoaded: payload.result.length < payload.pageSize,
    pagination: {
      pageNumber: payload.pageNumber,
      pageSize: payload.pageSize,
    },
  }),
}));

export const loadMoreTenants = builder.createServerAction(() => ({
  name: 'loadMoreTenants',
  url: tenantEndpoint(),
  body: (state: State) => ({
    pageNumber: getPageNumberAfterLoadMore(
      state.tenant.tenantsList,
      state.tenant.pagination.pageSize,
    ),
    ...state.tenant.filters,
  }),
  method: httpMethod.post,
  onSuccess: (
    state: TenantState,
    payload: APITenantSearchResult,
  ) => ({
    tenantsList: [...(state.tenantsList || []), ...payload.result],
    tenantsListLoaded: true,
    allTenantsLoaded: payload.result.length < payload.pageSize,
    pagination: {
      pageNumber: payload.pageNumber,
      pageSize: payload.pageSize,
    },
  }),
}));

export const loadTenant = builder.createServerAction(
  (tenantId: string) => ({
    name: 'loadTenant',
    url: tenantSingleEndpoint(tenantId),
    method: httpMethod.get,
    onSuccess: (state: TenantState, payload: APITenant) => ({
      tenant: payload,
    }),
  }),
);

export const deleteTenant = builder.createServerAction(
  (tenantId: string) => ({
    name: 'deleteTenant',
    url: tenantSingleEndpoint(tenantId),
    method: httpMethod.delete,
    onSuccess: (state: TenantState) => ({
      tenantsList: state.tenantsList.filter(
        tenant => tenant.id !== tenantId,
      ),
    }),
  }),
);

export const updateTenant = builder.createServerAction(
  (tenantId: string, formData: FormData) => ({
    name: 'updateTenant',
    url: tenantSingleEndpoint(tenantId),
    method: httpMethod.post,
    body: formData,
    onSuccess: (state: TenantState, result: APITenant) => ({
      tenantsList: state.tenantsList.map(tenant =>
        tenant.id === tenantId ? result : tenant,
      ),
      tenant: result,
    }),
  }),
);

export const clearTenant = builder.createReduxAction(() => ({
  name: 'clearTenant',
  updater: () => ({
    tenant: null,
  }),
}));

export const createTenant = builder.createServerAction(
  (avatarFormData: FormData) => ({
    name: 'createTenant',
    url: tenantEndpoint(),
    method: httpMethod.put,
    body: avatarFormData,
    onSuccess: (state: TenantState, result: APITenant) => ({
      tenantsList: [result, ...state.tenantsList],
      tenant: result,
    }),
  }),
);

export const useTenant = () => {
  const dispatch = useDispatch();

  return useMemo(
    () =>
      bindActionCreators(
        {
          clearTenant,
          resetTenantFilters,
          clearTenantFilters,
          updateTenantFilters,
          createTenant,
          updateTenant,
          deleteTenant,
          loadTenant,
          loadTenants,
          loadMoreTenants,
        },
        dispatch,
      ),
    [dispatch],
  );
};

export const tenantReducer = builder.getReducers();
